import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { teamsAPI, playersAPI, seasonsAPI } from '../api/api';
import PlayerAvatar from '../components/PlayerAvatar';
import api from '../api/api';

const TeamRoster = () => {
  const { id } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [teamStats, setTeamStats] = useState(null);
  const [games, setGames] = useState([]);
  const [activeTab, setActiveTab] = useState('roster');
  const [playerStats, setPlayerStats] = useState([]);
  const [playerStatsLoading, setPlayerStatsLoading] = useState(false);
  // 'regular' | 'playoffs' | 'all' — Playoffspiele zaehlen standardmaessig nicht mit.
  const [statsScope, setStatsScope] = useState('regular');
  const [statsMode, setStatsMode] = useState('averages');
  const [statsSort, setStatsSort] = useState({ key: 'points', dir: 'desc' });
  const [loading, setLoading] = useState(true);

  const loadSeasons = async () => {
    try {
      const response = await seasonsAPI.getAll();
      setSeasons(response.data);
      // Default auf aktive Saison — sonst auf die neueste (höchste id), damit
      // die Stats-/Game-History-Tabs auch ohne aktive Saison Daten zeigen.
      const activeSeason = response.data.find(s => s.isActive);
      const defaultSeason = activeSeason
        || [...response.data].sort((a, b) => b.id - a.id)[0];
      if (defaultSeason) {
        setSelectedSeason(defaultSeason.id.toString());
      }
    } catch (error) {
      console.error('Error loading seasons:', error);
    }
  };

  const loadTeamStats = async () => {
    if (!selectedSeason) return;
    try {
      const response = await teamsAPI.getStats(id, selectedSeason);
      setTeamStats(response.data);
    } catch (error) {
      console.error('Error loading team stats:', error);
    }
  };

  const loadPlayerStats = async () => {
    if (!selectedSeason) return;
    setPlayerStatsLoading(true);
    try {
      const response = await teamsAPI.getPlayerStats(id, selectedSeason, statsScope);
      setPlayerStats(response.data.players || []);
    } catch (error) {
      console.error('Error loading team player stats:', error);
      setPlayerStats([]);
    } finally {
      setPlayerStatsLoading(false);
    }
  };

  const loadGames = async () => {
    if (!selectedSeason) return;
    try {
      const response = await teamsAPI.getGames(id, selectedSeason);
      setGames(response.data);
    } catch (error) {
      console.error('Error loading games:', error);
    }
  };

  const loadTeamData = async (seasonId = null) => {
    try {
      // Get active season if no seasonId provided
      let finalSeasonId = seasonId;
      if (!finalSeasonId && seasons.length > 0) {
        const activeSeason = seasons.find(s => s.isActive);
        finalSeasonId = activeSeason ? activeSeason.id : null;
      }


      const teamApiCall = finalSeasonId 
        ? api.get(`/teams/${id}?seasonId=${finalSeasonId}`)
        : teamsAPI.getById(id);

      const playersApiCall = finalSeasonId
        ? api.get(`/players/team/${id}?seasonId=${finalSeasonId}`)
        : playersAPI.getByTeam(id);

      const [teamRes, playersRes] = await Promise.all([
        teamApiCall,
        playersApiCall
      ]);
      
      setTeam(teamRes.data);
      setPlayers(playersRes.data);
      
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSeasons();
  }, [id]);

  useEffect(() => {
    if (seasons.length > 0) {
      loadTeamData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, seasons]);

  useEffect(() => {
    if (selectedSeason && activeTab !== 'roster') {
      if (activeTab === 'stats') {
        loadTeamStats();
      } else if (activeTab === 'playerStats') {
        loadPlayerStats();
      } else if (activeTab === 'games') {
        loadGames();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason, activeTab, statsScope]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-2xl text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Team not found</h1>
          <Link to="/teams" className="text-nba-blue hover:underline">
            Back to Teams
          </Link>
        </div>
      </div>
    );
  }

  // Inaktive Spieler liefert der Server hier nicht mit — umgeschaltet wird sie
  // in der Kaderverwaltung im Admin-Bereich (/admin/roster).
  const positionOrder = { 'PG': 1, 'SG': 2, 'SF': 3, 'PF': 4, 'C': 5 };
  const sortedPlayers = [...players].sort((a, b) => {
    const ovrDiff = (b.overallRating || 0) - (a.overallRating || 0);
    if (ovrDiff !== 0) return ovrDiff;
    return (positionOrder[a.position] || 99) - (positionOrder[b.position] || 99);
  });

  // Spalten des "Spieler Stats"-Tabs. pct = Prozentwert, unabhaengig von
  // Schnitt/Gesamt. Die uebrigen Werte kommen je nach Modus aus averages/totals.
  const statColumns = [
    { key: 'games', label: 'GP', integer: true },
    { key: 'minutes', label: 'MIN' },
    { key: 'points', label: 'PTS' },
    { key: 'rebounds', label: 'REB' },
    { key: 'assists', label: 'AST' },
    { key: 'steals', label: 'STL' },
    { key: 'blocks', label: 'BLK' },
    { key: 'turnovers', label: 'TO' },
    { key: 'fieldGoal', label: 'FG%', pct: true },
    { key: 'threePoint', label: '3P%', pct: true },
    { key: 'freeThrow', label: 'FT%', pct: true }
  ];

  const statValue = (row, column) => {
    if (column.key === 'games') return row.games;
    if (column.pct) return row.percentages[column.key];
    return (statsMode === 'totals' ? row.totals : row.averages)[column.key];
  };

  const formatStat = (row, column) => {
    const value = statValue(row, column);
    if (value === null || value === undefined) return '—';
    if (column.pct) return `${value.toFixed(1)}%`;
    if (column.integer || statsMode === 'totals') return Math.round(value);
    return value.toFixed(1);
  };

  const toggleStatSort = (key) => {
    setStatsSort((prev) => (
      prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    ));
  };

  const sortColumn = statColumns.find(c => c.key === statsSort.key) || statColumns[2];
  // Spieler ohne Wert in der Sortierspalte (z.B. kein Dreierversuch) stehen immer hinten.
  const sortedPlayerStats = [...playerStats].sort((a, b) => {
    const av = statValue(a, sortColumn);
    const bv = statValue(b, sortColumn);
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    return statsSort.dir === 'desc' ? bv - av : av - bv;
  });

  const renderPlayerRow = (player) => (
    <tr key={player.id} className="border-b hover:bg-gray-50 dark:bg-gray-800">
      <td className="px-4 py-4">
        <Link
          to={`/player/${player.id}`}
          className="flex items-center gap-3 group"
        >
          <PlayerAvatar player={player} size="small" />
          <div className="font-semibold text-nba-blue group-hover:text-nba-red group-hover:underline transition-colors">
            {player.firstName} {player.lastName}
          </div>
        </Link>
      </td>
      <td className="px-4 py-4 text-center font-semibold">
        {player.jerseyNumber || '-'}
      </td>
      <td className="px-4 py-4 text-center">
        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
          {player.position}
        </span>
      </td>
      <td className="px-4 py-4 text-center">
        <span className={`text-lg font-bold ${
          player.overallRating >= 85 ? 'text-green-600 dark:text-green-400' :
          player.overallRating >= 80 ? 'text-blue-600 dark:text-blue-400' :
          player.overallRating >= 75 ? 'text-yellow-600' :
          'text-gray-600 dark:text-gray-400'
        }`}>
          {player.overallRating}
        </span>
      </td>
      <td className="px-4 py-4 text-center">
        {player.badges ? (
          <span className="text-yellow-600 font-semibold">
            {player.badges}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="px-4 py-4 text-center">
        <a
          href={get2KRatingsUrl(player)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-800 font-medium text-sm transition"
          title="View on 2K Ratings"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Profile
        </a>
      </td>
    </tr>
  );

  const get2KRatingsUrl = (player) => {
    if (!player) return null;
    const playerName = `${player.firstName}-${player.lastName}`.toLowerCase();
    return `https://www.2kratings.com/${playerName}`;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Link to="/teams" className="text-nba-blue hover:underline mb-4 inline-block">
        ← Back to All Teams
      </Link>

      {/* Team Header */}
      <div 
        className="rounded-lg shadow-lg dark:shadow-gray-900 p-8 mb-8 text-white"
        style={{ backgroundColor: team.primaryColor || '#1d428a' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img 
              src={`/images/teams/${team.name.toLowerCase().replace(/\s/g, '')}.svg`} 
              alt={`${team.name} logo`}
              className="w-24 h-24 md:w-32 md:h-32 object-contain bg-white dark:bg-gray-800 bg-opacity-20 rounded-lg p-2"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <h1 className="text-4xl font-bold mb-2">{team.city} {team.name}</h1>
              <p className="text-xl">{team.conference} Conference</p>
            </div>
          </div>
          <div className="text-6xl font-bold opacity-50">
            {team.abbreviation}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-t-lg shadow-lg dark:shadow-gray-900">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('roster')}
              className={`py-4 px-8 font-semibold border-b-2 transition-colors ${
                activeTab === 'roster'
                  ? 'border-nba-blue text-nba-blue'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              Roster
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-4 px-8 font-semibold border-b-2 transition-colors ${
                activeTab === 'stats'
                  ? 'border-nba-blue text-nba-blue'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              Team Stats
            </button>
            <button
              onClick={() => setActiveTab('playerStats')}
              className={`py-4 px-8 font-semibold border-b-2 transition-colors ${
                activeTab === 'playerStats'
                  ? 'border-nba-blue text-nba-blue'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              Spieler Stats
            </button>
            <button
              onClick={() => setActiveTab('games')}
              className={`py-4 px-8 font-semibold border-b-2 transition-colors ${
                activeTab === 'games'
                  ? 'border-nba-blue text-nba-blue'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              Game History
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-b-lg shadow-lg dark:shadow-gray-900 p-6">
        {/* Season Selector for Stats and Games tabs */}
        {(activeTab === 'stats' || activeTab === 'playerStats' || activeTab === 'games') && seasons.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Season
            </label>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-nba-blue dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              {seasons.map(season => (
                <option key={season.id} value={season.id}>
                  {season.year} {season.isActive && '(Current)'}
                </option>
              ))}
            </select>
          </div>
        )}

        {activeTab === 'roster' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Team Roster</h2>
            
            {players.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <p className="text-xl">No players on roster yet</p>
                <p className="mt-2">Players will be added soon!</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left">Player</th>
                        <th className="px-4 py-3 text-center">Jersey</th>
                        <th className="px-4 py-3 text-center">Position</th>
                        <th className="px-4 py-3 text-center">Overall</th>
                        <th className="px-4 py-3 text-center">Badges</th>
                        <th className="px-4 py-3 text-center">2K Ratings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPlayers.map(renderPlayerRow)}
                    </tbody>
                  </table>
                </div>

                {/* Roster Stats */}
                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-nba-blue">{players.length}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Players</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-nba-blue">
                      {(sortedPlayers.reduce((sum, p) => sum + (p.overallRating || 0), 0) / sortedPlayers.length).toFixed(1)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Avg Overall</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-nba-blue">
                      {Math.max(...sortedPlayers.map(p => p.overallRating || 0))}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Highest Rated</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-nba-blue">
                      {Math.min(...sortedPlayers.map(p => p.overallRating || 0))}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Lowest Rated</div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'stats' && teamStats && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Season Statistics</h2>
            
            {/* Record */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Record</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-4xl font-bold text-nba-blue">
                    {teamStats.record.wins}-{teamStats.record.losses}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Overall</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-nba-blue">
                    {teamStats.record.winPercentage}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Win Percentage</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-nba-blue">
                    {teamStats.record.homeRecord}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Home Record</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-nba-blue">
                    {teamStats.record.awayRecord}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Away Record</div>
                </div>
              </div>
            </div>

            {/* Scoring */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Scoring</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {teamStats.averages.pointsPerGame}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Points Per Game</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {teamStats.averages.pointsAllowedPerGame}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Points Allowed</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className={`text-3xl font-bold ${
                    parseFloat(teamStats.averages.pointDifferential) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {parseFloat(teamStats.averages.pointDifferential) > 0 ? '+' : ''}
                    {teamStats.averages.pointDifferential}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Point Differential</div>
                </div>
              </div>
            </div>

            {/* Shooting */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Shooting</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-nba-blue">
                    {teamStats.averages.fieldGoalPercentage}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">FG%</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-nba-blue">
                    {teamStats.averages.threePointPercentage}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">3P%</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-nba-blue">
                    {teamStats.averages.freeThrowPercentage}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">FT%</div>
                </div>
              </div>
            </div>

            {/* Other Stats */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Per Game Averages</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-nba-blue">
                    {teamStats.averages.reboundsPerGame}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Rebounds</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-nba-blue">
                    {teamStats.averages.assistsPerGame}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Assists</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-nba-blue">
                    {teamStats.averages.stealsPerGame}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Steals</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-nba-blue">
                    {teamStats.averages.blocksPerGame}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Blocks</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-nba-blue">
                    {teamStats.averages.turnoversPerGame}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Turnovers</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'playerStats' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Spieler Stats</h2>

            <div className="flex flex-wrap gap-4 mb-6">
              <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                {[
                  { value: 'regular', label: 'Regular Season' },
                  { value: 'playoffs', label: 'Playoffs' },
                  { value: 'all', label: 'Gesamt' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setStatsScope(option.value)}
                    className={`px-4 py-2 text-sm font-semibold transition-colors ${
                      statsScope === option.value
                        ? 'bg-nba-blue text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                {[
                  { value: 'averages', label: 'Per Game' },
                  { value: 'totals', label: 'Totals' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setStatsMode(option.value)}
                    className={`px-4 py-2 text-sm font-semibold transition-colors ${
                      statsMode === option.value
                        ? 'bg-nba-blue text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {playerStatsLoading ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading...</div>
            ) : sortedPlayerStats.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <p className="text-xl">Keine Statistiken für diese Auswahl</p>
                <p className="mt-2">Sobald Spiele erfasst sind, erscheinen sie hier.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left">Player</th>
                      <th className="px-4 py-3 text-center">Pos</th>
                      {statColumns.map(column => (
                        <th
                          key={column.key}
                          onClick={() => toggleStatSort(column.key)}
                          className={`px-3 py-3 text-center cursor-pointer select-none whitespace-nowrap hover:text-nba-blue ${
                            statsSort.key === column.key ? 'text-nba-blue' : ''
                          }`}
                        >
                          {column.label}
                          {statsSort.key === column.key && (statsSort.dir === 'desc' ? ' ▼' : ' ▲')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPlayerStats.map(row => (
                      <tr key={row.player.id} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                        <td className="px-4 py-3">
                          <Link
                            to={`/player/${row.player.id}`}
                            className="flex items-center gap-3 group"
                          >
                            <PlayerAvatar player={row.player} size="small" />
                            <div className="font-semibold text-nba-blue group-hover:text-nba-red group-hover:underline transition-colors whitespace-nowrap">
                              {row.player.firstName} {row.player.lastName}
                            </div>
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {row.player.position}
                          </span>
                        </td>
                        {statColumns.map(column => (
                          <td
                            key={column.key}
                            className={`px-3 py-3 text-center whitespace-nowrap ${
                              statsSort.key === column.key ? 'font-bold text-nba-blue' : ''
                            }`}
                          >
                            {formatStat(row, column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'games' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Game History</h2>
            
            {games.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <p className="text-xl">No games found</p>
                <p className="mt-2">Games will appear here once they are played</p>
              </div>
            ) : (
              <div className="space-y-3">
                {games.map((game) => {
                  const isHome = game.homeTeamId === parseInt(id);
                  const teamScore = isHome ? game.homeScore : game.awayScore;
                  const oppScore = isHome ? game.awayScore : game.homeScore;
                  const opponent = isHome ? game.awayTeam : game.homeTeam;
                  const won = teamScore > oppScore;

                  return (
                    <Link
                      key={game.id}
                      to={`/game/${game.id}`}
                      className="block bg-gray-50 dark:bg-gray-800 rounded-lg p-4 hover:bg-gray-100 dark:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`font-bold px-3 py-1 rounded ${
                            won ? 'bg-green-100 dark:bg-green-900/30 text-green-800' : 'bg-red-100 dark:bg-red-900/30 text-red-800'
                          }`}>
                            {won ? 'W' : 'L'}
                          </div>
                          <div>
                            <div className="font-semibold">
                              {isHome ? 'vs' : '@'} {opponent.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {new Date(game.gameDate).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">
                            {teamScore} - {oppScore}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {game.season?.year}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamRoster;
